import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiChatConversationMessageType } from '@fi-shared/fi-chat/fi-chat-conversation-message-type';
import { FileType } from '@fi-shared/types';
import { FiChatConversationType } from '@fi-shared/fi-chat/fi-chat-conversation-type';
import {
    IChatConversationMenuOption,
    IChatConversationMessage,
    IChatConversationUser,
} from '@fi-shared/fi-chat/fi-chat';
import { MatMenuTrigger } from '@angular/material/menu';
import { AppTextService } from '@fi-shared/app-text';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { isDefined } from '@fi-shared/utils';
import { FiChatConversationMessageActionsService } from '@fi-shared/fi-chat/fi-chat-conversation/fi-chat-conversation-message-actions.service';

@Component({
    selector: 'fi-message-menu-button',
    templateUrl: './message-menu-button.component.html',
    styleUrl: './message-menu-button.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-message-menu-button' },
    standalone: false,
})
export class FiMessageMenuButtonComponent implements OnInit {
    @Input({ required: true }) conversationMessage: IChatConversationMessage;
    @Input({ required: true }) isOutgoingMessage: boolean;
    @Input({ required: true }) conversationId: number;
    @Input({ required: true }) conversationUsers: IChatConversationUser[];
    @Input() conversationType: FiChatConversationType;
    @Input() sourceMessageType: FiChatConversationMessageType;
    @Input() isConversationMessageInfoShown: boolean;

    @ViewChild('messageSettingsButton', { read: ElementRef, static: true }) messageSettingsButtonRef: ElementRef;
    @ViewChild('messageSettingsButton', {
        read: MatMenuTrigger,
        static: true,
    })
    messageSettingsMenuButton: MatMenuTrigger;
    @Input({ required: true, alias: 'isMouseOverContainer' }) set isMouseOverContainer(value: boolean) {
        this._isMouseOverContainer = value;
        this.toggleButtonSettingsClass();
    }

    @Input({ required: true, alias: 'triggerMessageMenuOpen' }) set openMenu(value: void) {
        this.toggleButtonSettingsClass();
        this.messageSettingsMenuButton.openMenu();
    }

    protected messageOptions: IChatConversationMenuOption[] = [];
    public isMatMenuOpened: boolean;
    private _isMouseOverContainer: boolean;

    constructor(
        private appTextService: AppTextService,
        private fiChatHelperService: FiChatHelperService,
        private renderer: Renderer2,
        private fiChatConversationMessageActionsService: FiChatConversationMessageActionsService,
    ) {}

    ngOnInit(): void {
        this.setMessageOptions();
    }

    protected setIsMatMenuOpened(isOpened: boolean) {
        this.isMatMenuOpened = isOpened;
        this.toggleButtonSettingsClass();
    }

    private setMessageOptions(): void {
        this.messageOptions = [
            {
                label: this.appTextService.get('common.reply'),
                iconClass: 'fas fa-reply',
                action: () =>
                    this.fiChatConversationMessageActionsService.reply(this.conversationId, this.conversationMessage),
                isAvailable: !this.isReplyOptionsDisabled(),
            },
            {
                label: this.appTextService.get('common.edit'),
                iconClass: 'fas fa-pen-to-square',
                action: () =>
                    this.fiChatConversationMessageActionsService.edit(this.conversationId, this.conversationMessage),
                isAvailable: this.isEditMessageOptionAvailable(),
            },
            {
                label: this.appTextService.get('copy'),
                iconClass: 'fas fa-clone',
                action: () => this.fiChatConversationMessageActionsService.copy(this.conversationMessage),
                isAvailable: this.isCopyMessageOptionAvailable(),
            },
            {
                label: this.appTextService.get('chat.forward'),
                iconClass: 'fas fa-share',
                action: () =>
                    this.fiChatConversationMessageActionsService.forward(this.conversationId, this.conversationMessage),
                isAvailable: !this.isForwardOptionsDisabled(),
            },
            {
                label: this.appTextService.get('common.delete'),
                iconClass: 'far fa-trash',
                action: () =>
                    this.fiChatConversationMessageActionsService.delete(this.conversationId, this.conversationMessage),
                isAvailable: this.isDeleteOptionAvailable(),
                menuItemClass: 'delete-button',
            },
            {
                label: this.appTextService.get('chat.delete_for_you'),
                iconClass: 'far fa-trash',
                action: () =>
                    this.fiChatConversationMessageActionsService.deleteForUser(
                        this.conversationId,
                        this.conversationMessage,
                    ),
                isAvailable: !this.isOutgoingMessage,
                menuItemClass: 'delete-button',
            },
        ];
    }

    private isOneToOneUserDeleted(): boolean {
        const result =
            this.conversationType === FiChatConversationType.OneToOne &&
            !!this.conversationUsers.find((conversationUser) => conversationUser.IsDeleted);
        return result;
    }

    private isForwardOptionsDisabled(): boolean {
        return (
            this.conversationMessage.Type === FiChatConversationMessageType.Deleted ||
            (this.isOnlySourceMessage() && this.sourceMessageType === FiChatConversationMessageType.Deleted)
        );
    }

    private isReplyOptionsDisabled(): boolean {
        return (
            this.isOneToOneUserDeleted() ||
            this.conversationMessage.Type === FiChatConversationMessageType.Deleted ||
            (this.isOnlySourceMessage() && this.sourceMessageType === FiChatConversationMessageType.Deleted)
        );
    }

    private isDeleteOptionAvailable(): boolean {
        return (
            this.isOutgoingMessage &&
            (this.conversationMessage.HasDeleteAccess ||
                (this.isOnlySourceMessage() && this.sourceMessageType === FiChatConversationMessageType.Deleted))
        );
    }

    private isEditMessageOptionAvailable(): boolean {
        return (
            !this.isOneToOneUserDeleted() &&
            this.isOutgoingMessage &&
            this.conversationMessage.HasEditAccess &&
            !this.conversationMessage.Links.length
        );
    }

    private isCopyMessageOptionAvailable(): boolean {
        return (
            !!this.conversationMessage.Content?.length ||
            (this.conversationMessage.MediaBankFileReferences?.length &&
                this.conversationMessage.MediaBankFileReferences[0].FileType === FileType.Image)
        );
    }

    private toggleButtonSettingsClass(): void {
        if (!isDefined(this.messageSettingsButtonRef)) return;

        if (this.isMatMenuShown()) {
            this.renderer.addClass(this.messageSettingsButtonRef.nativeElement, 'menu-shown');
        } else {
            this.renderer.removeClass(this.messageSettingsButtonRef.nativeElement, 'menu-shown');
        }
    }

    private isOnlySourceMessage(): boolean {
        return this.fiChatHelperService.isOnlySourceMessage(this.conversationMessage);
    }

    private isMatMenuShown(): boolean {
        return this.isMatMenuOpened || this._isMouseOverContainer;
    }
}
